import React from 'react';
import { Platform, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * WarningBar: the yellow bar for site requirements and important location
 * notes only (sign-in, access pass, PPE), never tickets (SPEC decision 8).
 * Props:
 * - items: array of strings (e.g. getDocketAccessRequirementLabels(docket)) or
 *   { text, icon } objects (Feather icon name; default 'alert-triangle')
 * - size: 'regular' (stop card, 14) | 'compact' (run list, 13)
 * - attached: 'bottom' squares the bottom corners so the bar joins the row under it
 * - tone: 'warning' (default) | 'danger' (red, e.g. important notes on the stop screen)
 * - style
 */
const WarningBar = ({ items, size = 'regular', attached, tone = 'warning', style }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isCompact = size === 'compact';
	const isDanger = tone === 'danger';
	const rows = (items || []).filter(Boolean).map(item => (typeof item === 'string' ? { text: item } : item));

	if (!rows.length) {
		return null;
	}

	return (
		<View
			style={[styles.bar, isDanger && styles.barDanger, isCompact && styles.barCompact, attached === 'bottom' && styles.attachedBottom, style]}
			accessible={true}
			accessibilityLabel={`Warning: ${rows.map(row => row.text).join('. ')}`}
		>
			{rows.map((row, index) => (
				<View key={`${index}-${row.text}`} style={styles.item}>
					<Feather
						name={row.icon || 'alert-triangle'}
						size={isCompact ? 18 : 20}
						color={isDanger ? colors.danger : colors.warning}
						style={styles.icon}
					/>
					<Text style={[styles.text, isCompact && styles.textCompact, isDanger && styles.textDanger]}>{row.text}</Text>
				</View>
			))}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	bar: {
		backgroundColor: colors.warningBg,
		borderWidth: 1,
		borderColor: colors.warningBorder,
		borderRadius: radius.control,
		paddingVertical: space.md,
		paddingHorizontal: 14,
		gap: 10,
	},
	barDanger: {
		backgroundColor: colors.dangerBg,
		borderColor: colors.dangerBorder,
	},
	barCompact: {
		paddingVertical: space.sm,
		paddingHorizontal: space.md,
		gap: 6,
	},
	attachedBottom: {
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		borderBottomWidth: 0,
	},
	item: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 10,
	},
	icon: {
		// Phone text is cap-centred by the shared Text, so the icon needs no nudge there.
		marginTop: Platform.OS === 'web' ? 1 : 0,
	},
	text: {
		...type.bodySmallStrong,
		color: colors.warning,
		flex: 1,
	},
	textCompact: {
		...type.secondaryStrong,
	},
	textDanger: {
		color: colors.danger,
	},
});

export default WarningBar;
